<script setup>
import { usePage } from '@inertiajs/vue3'
import HomeController from '@/actions/App/Http/Controllers/HomeController'
import AboutController from '@/actions/App/Http/Controllers/AboutController'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import { plumbing, heating, gas, maintenance, boilerReplacement, heatPumps } from '@/actions/App/Http/Controllers/ServiceController'
import { privacy, cookies } from '@/actions/App/Http/Controllers/LawController'
import FooterLudwig from '@/layouts/partials/FooterLudwig.vue'
</script>

<template>
    <footer class="bg-gray-50">
        <div class="section-width mx-auto py-12">
            <div class="w-full px-2 md:-mx-3 md:flex md:items-center md:justify-between lg:px-0">
                <div class="mt-6 grid w-full shrink-0 grid-cols-1 md:mx-3 md:mt-0 md:w-auto">
                    <Link
                        :href="contactIndex().url + '#demand-form'"
                        aria-label="Objednávkový formulář"
                        class="primary-link inline-flex w-full! items-center rounded-full text-base"
                    >
                        Objednávkový formulář
                    </Link>
                </div>
            </div>

            <hr class="my-6 border-gray-200 md:my-10" />

            <div class="grid grid-cols-1 gap-6 px-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 lg:px-0">
                <div>
                    <p class="font-semibold text-gray-800">Stránky</p>

                    <div class="mt-5 flex flex-col items-start space-y-2">
                        <Link
                            :href="HomeController().url"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Domů
                        </Link>
                        <Link
                            :href="AboutController().url"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            O nás
                        </Link>
                        <Link
                            :href="projectIndex().url"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Naše realizace
                        </Link>
                        <Link
                            :href="contactIndex().url"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Kontakty
                        </Link>
                    </div>
                </div>

                <div>
                    <p class="font-semibold text-gray-800">Služby</p>

                    <div class="mt-5 flex flex-col items-start space-y-2">
                        <Link
                            :href="plumbing().url"
                            aria-label="Odkaz na službu Vodoinstalace"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Vodoinstalace
                        </Link>
                        <Link
                            :href="heating().url"
                            aria-label="Odkaz na službu Topenářství"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Topenářství
                        </Link>
                        <Link
                            :href="gas().url"
                            aria-label="Odkaz na službu Plynoinstalace"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Plynoinstalace
                        </Link>
                        <Link
                            :href="maintenance().url"
                            aria-label="Odkaz na službu Servis / Sezonní údržba"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Servis / Sezonní údržba
                        </Link>
                        <Link
                            :href="boilerReplacement().url"
                            aria-label="Odkaz na službu Výměna kotlů"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Výměna kotlů
                        </Link>
                        <Link
                            :href="heatPumps().url"
                            aria-label="Odkaz na službu Tepelná čerpadla"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Tepelná čerpadla
                        </Link>
                    </div>
                </div>

                <div>
                    <p class="font-semibold text-gray-800">Kontakty</p>

                    <div class="mt-5 flex flex-col items-start space-y-2">
                        <p>
                            {{ usePage().props.address?.street }}, {{ usePage().props.address?.city }},
                            {{ usePage().props.address?.zip }}
                        </p>
                        <a
                            :href="'tel:' + usePage().props.contacts?.phone?.replace(/\s/g, '')"
                            :aria-label="'Odkaz na telefonní číslo ' + usePage().props.contacts?.phone"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            {{ usePage().props.contacts?.phone ?? '+420 123 456 789' }}
                        </a>
                        <a
                            :href="'mailto:' + usePage().props.contacts?.email"
                            :aria-label="'Odkaz na email ' + usePage().props.contacts?.email"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            {{ usePage().props.contacts?.email ?? 'info@kalianko.cz' }}
                        </a>
                        <a
                            href="https://rejstrik-firem.kurzy.cz/03361241/kalianko-s-r-o/"
                            target="_blank"
                            :aria-label="'Odkaz na IČ ' + usePage().props.contacts?.ico"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            IČ: {{ usePage().props.contacts?.ico ?? '03361241' }}
                        </a>
                        <a
                            href="https://rejstrik-firem.kurzy.cz/03361241/kalianko-s-r-o/"
                            target="_blank"
                            :aria-label="'Odkaz na DIČ ' + usePage().props.contacts?.dic"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            DIČ: {{ usePage().props.contacts?.dic ?? '	CZ03361241' }}
                        </a>
                    </div>
                </div>

                <div>
                    <p class="font-semibold text-gray-800">Dokumenty a nastavení</p>

                    <div class="mt-5 flex flex-col items-start space-y-2">
                        <Link
                            :href="privacy().url"
                            aria-label="Odkaz na Zásady ochrany osobních údajů"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Zásady ochrany osobních údajů
                        </Link>
                        <Link
                            :href="cookies().url"
                            aria-label="Odkaz na Zásady používání souborů cookie"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Zásady používání souborů cookie
                        </Link>
                        <button
                            aria-label="Změnit preference souborů cookie"
                            onclick="showHideToggleCookiePreferencesModal()"
                            class="text-gray-600 transition-colors duration-300 hover:text-blue-500 hover:underline"
                        >
                            Změnit preference souborů cookie
                        </button>
                    </div>
                </div>
            </div>

            <hr class="my-6 border-gray-200 md:my-10" />

            <div class="flex flex-col items-center justify-between sm:flex-row">
                <a href="#">
                    <img
                        class="h-7 w-auto"
                        src="/images/kalianko-logo.png"
                        alt="Kalianko logo"
                    />
                </a>

                <p class="mt-4 text-sm text-gray-500 sm:mt-0">
                    {{ new Date().getFullYear() }} © KALIANKO s.r.o., všechna práva vyhrazena.
                </p>
            </div>

            <hr class="my-6 border-gray-200 md:mt-10 md:mb-8" />
            <FooterLudwig />
        </div>
    </footer>
</template>
